import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { CardShell, seriesColor, fmt, asNumber, asText } from './shared';

export interface ChartSpec {
  tipo: 'grafico';
  titulo?: string;
  formato?: 'barras' | 'linhas' | 'pizza';
  unidade?: string;
  categorias?: unknown[];
  series?: { nome?: string; valores?: unknown[] }[];
  fonte?: string;
}

const W = 640;
const H = 260;
const PAD = { top: 16, right: 12, bottom: 44, left: 52 };

/** Ticks "redondos" de 0 (ou do mínimo negativo) até o máximo. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  const lo = Math.min(0, min);
  const hi = max <= lo ? lo + 1 : max;
  const raw = (hi - lo) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || raw;
  const start = Math.floor(lo / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= hi + step * 0.001; v += step) ticks.push(Number(v.toFixed(10)));
  if (ticks[ticks.length - 1] < hi) ticks.push(ticks[ticks.length - 1] + step);
  return ticks;
}

const short = (s: string, n = 14) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function Chart({ spec }: { spec: ChartSpec }) {
  const theme: any = useTheme();
  const [hover, setHover] = useState<string | null>(null);
  const cats = (spec.categorias || []).map(asText);
  const series = (spec.series || []).map((s, i) => ({
    nome: asText(s.nome) || `Série ${i + 1}`,
    valores: cats.map((_, j) => asNumber((s.valores || [])[j])),
  }));
  const unit = spec.unidade ? ` ${spec.unidade}` : '';
  if (!cats.length || !series.length) return null;

  const axis = theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)';
  const label = theme.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';

  if (spec.formato === 'pizza') {
    const values = series[0].valores.map((v) => (Number.isFinite(v) && v > 0 ? v : 0));
    const total = values.reduce((a, b) => a + b, 0) || 1;
    let angle = -Math.PI / 2;
    const R = 92;
    const r = 56;
    const cx = 120;
    const cy = 120;
    const arcs = values.map((v, i) => {
      const a0 = angle;
      const a1 = angle + (v / total) * Math.PI * 2;
      angle = a1;
      const large = a1 - a0 > Math.PI ? 1 : 0;
      const p = (rad: number, a: number) => `${cx + rad * Math.cos(a)} ${cy + rad * Math.sin(a)}`;
      const d = v >= total
        ? `M ${p(R, 0)} A ${R} ${R} 0 1 1 ${p(R, Math.PI)} A ${R} ${R} 0 1 1 ${p(R, 0)} M ${p(r, 0)} A ${r} ${r} 0 1 0 ${p(r, Math.PI)} A ${r} ${r} 0 1 0 ${p(r, 0)}`
        : `M ${p(R, a0)} A ${R} ${R} 0 ${large} 1 ${p(R, a1)} L ${p(r, a1)} A ${r} ${r} 0 ${large} 0 ${p(r, a0)} Z`;
      return { d, i, v };
    });
    return (
      <CardShell title={spec.titulo} source={spec.fonte} label="grafico">
        <PieLayout>
          <svg viewBox="0 0 240 240" role="img" aria-label={spec.titulo || 'Gráfico de pizza'} style={{ width: 200, maxWidth: '45%' }}>
            {arcs.map(({ d, i, v }) => (
              <path key={i} d={d} fill={seriesColor(theme.mode, i)} fillRule="evenodd"
                opacity={hover === null || hover === cats[i] ? 1 : 0.35}
                onMouseEnter={() => setHover(cats[i])} onMouseLeave={() => setHover(null)}>
                <title>{`${cats[i]}: ${fmt(v)}${unit} (${fmt((v / total) * 100, 1)}%)`}</title>
              </path>
            ))}
          </svg>
          <Legend $column>
            {cats.map((c, i) => (
              <LegendItem key={i} onMouseEnter={() => setHover(c)} onMouseLeave={() => setHover(null)}>
                <Swatch style={{ background: seriesColor(theme.mode, i) }} />
                <span>{c}</span>
                <Value>{fmt(values[i])}{unit} · {fmt((values[i] / total) * 100, 1)}%</Value>
              </LegendItem>
            ))}
          </Legend>
        </PieLayout>
      </CardShell>
    );
  }

  const all = series.flatMap((s) => s.valores).filter(Number.isFinite);
  const ticks = niceTicks(Math.min(...all), Math.max(...all));
  const yMin = ticks[0];
  const yMax = ticks[ticks.length - 1];
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const y = (v: number) => PAD.top + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH;
  const band = plotW / cats.length;
  const xCenter = (j: number) => PAD.left + band * j + band / 2;
  const isLine = spec.formato === 'linhas';
  const groupW = Math.min(band * 0.72, 22 * series.length + 8);
  const barW = groupW / series.length;
  const showValues = !isLine && cats.length * series.length <= 12;
  const labelEvery = Math.ceil(cats.length / 10);

  return (
    <CardShell title={spec.titulo} source={spec.fonte} label="grafico">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={spec.titulo || 'Gráfico'} style={{ width: '100%', height: 'auto' }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} stroke={axis} strokeDasharray={t === 0 ? undefined : '3 4'} />
            <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill={label}>{fmt(t)}</text>
          </g>
        ))}
        {cats.map((c, j) => (j % labelEvery === 0 ? (
          <text key={c + j} x={xCenter(j)} y={H - PAD.bottom + 18} textAnchor="middle" fontSize="11" fill={label}>
            <title>{c}</title>{short(c)}
          </text>
        ) : null))}
        {isLine ? series.map((s, i) => {
          const pts = s.valores.map((v, j) => (Number.isFinite(v) ? `${xCenter(j)},${y(v)}` : null)).filter(Boolean).join(' ');
          return (
            <g key={s.nome} opacity={hover === null || hover === s.nome ? 1 : 0.25}>
              <polyline points={pts} fill="none" stroke={seriesColor(theme.mode, i)} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
              {s.valores.map((v, j) => (Number.isFinite(v) ? (
                <circle key={j} cx={xCenter(j)} cy={y(v)} r="3.5" fill={seriesColor(theme.mode, i)}>
                  <title>{`${s.nome} · ${cats[j]}: ${fmt(v)}${unit}`}</title>
                </circle>
              ) : null))}
            </g>
          );
        }) : series.map((s, i) => (
          <g key={s.nome} opacity={hover === null || hover === s.nome ? 1 : 0.25}>
            {s.valores.map((v, j) => {
              if (!Number.isFinite(v)) return null;
              const x = xCenter(j) - groupW / 2 + barW * i;
              const top = Math.min(y(v), y(0));
              const h = Math.max(1, Math.abs(y(v) - y(0)));
              return (
                <g key={j}>
                  <rect x={x + 1} y={top} width={Math.max(2, barW - 2)} height={h} rx="3" fill={seriesColor(theme.mode, i)}>
                    <title>{`${series.length > 1 ? `${s.nome} · ` : ''}${cats[j]}: ${fmt(v)}${unit}`}</title>
                  </rect>
                  {showValues && (
                    <text x={x + barW / 2} y={top - 5} textAnchor="middle" fontSize="10.5" fill={label}>{fmt(v)}</text>
                  )}
                </g>
              );
            })}
          </g>
        ))}
      </svg>
      {series.length > 1 && (
        <Legend>
          {series.map((s, i) => (
            <LegendItem key={s.nome} onMouseEnter={() => setHover(s.nome)} onMouseLeave={() => setHover(null)}>
              <Swatch style={{ background: seriesColor(theme.mode, i) }} />
              <span>{s.nome}</span>
            </LegendItem>
          ))}
        </Legend>
      )}
      {spec.unidade && <UnitNote>Valores em {spec.unidade}</UnitNote>}
    </CardShell>
  );
}

export default Chart;

const PieLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
`;

const Legend = styled.div<{ $column?: boolean }>`
  display: flex;
  flex-direction: ${({ $column }) => ($column ? 'column' : 'row')};
  flex-wrap: wrap;
  gap: ${({ $column }) => ($column ? '6px' : '6px 14px')};
  margin-top: 6px;
  font-size: 0.8rem;
  min-width: 0;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: default;
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
`;

const Value = styled.span`
  opacity: 0.6;
  font-variant-numeric: tabular-nums;
`;

const UnitNote = styled.div`
  font-size: 0.72rem;
  opacity: 0.55;
  margin-top: 4px;
`;
